import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { ReloadIcon } from "@radix-ui/react-icons";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForgotPassword } from "../hooks/useForgotPassword";
import { AUTH_VALIDATION_KEYS, AUTH_TOAST_KEYS, FORGOT_PASSWORD_FORM_FIELDS } from "../constants";

export default function ForgotPasswordForm() {
  const { t } = useTranslation();
  const { mutate: forgotPassword, isPending } = useForgotPassword();

  // Define the schema for form validation with translated messages
  const formSchema = z.object({
    email: z.string().email(t(AUTH_VALIDATION_KEYS.INVALID_EMAIL)),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      [FORGOT_PASSWORD_FORM_FIELDS.EMAIL]: "",
    },
  });

  function onSubmit(values) {
    const toastId = toast.loading(t(AUTH_TOAST_KEYS.SENDING_RESET_LINK));

    // Add current language to the request
    const requestData = {
      ...values,
      language: localStorage.getItem("i18nextLng") || "en",
    };

    forgotPassword(requestData, {
      onSuccess: (data) => {
        toast.success(data.message, {
          id: toastId,
        });
        form.reset();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || t(AUTH_TOAST_KEYS.RESET_LINK_ERROR), {
          id: toastId,
        });
      },
    });
  }

  return (
    <>
      <div className="text-center py-10">
        <h1 className="text-3xl font-bold">{t("auth.forgotPassword")}</h1>
        <p className="text-muted-foreground mt-2">
          {t("auth.forgotPasswordDescription")}
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4 flex flex-col justify-center"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("user.email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("auth.enter", {
                      entity: t("user.email"),
                    })}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={isPending} className="w-full">
            {isPending ? (
              <>
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                {t("main.loading")}
              </>
            ) : (
              t("auth.sendResetLink")
            )}
          </Button>
        </form>
      </Form>

      <div className="mt-6 text-center text-sm space-y-2">
        <div>
          {t("auth.rememberPassword")}{" "}
          <Link to="/login" className="underline text-primary">
            {t("auth.backToLogin")}
          </Link>
        </div>
        <div>
          {t("auth.dontHaveAccount")}{" "}
          <Link to="/register" className="underline text-primary">
            {t("auth.register")}
          </Link>
        </div>
      </div>
    </>
  );
}
